import { parseMarketOutcomes, parseOutcomePrices } from '@/utils/market-outcomes';
import { SPORT_CARD_VARIANT } from '@/constants/sportCardVariant.js';
import { getMatchId, parseMarketContest, resolveNumericMatchId } from '@/utils/marketContest.js';
import { pickPrimarySportMarket } from '@/utils/sport-collection-map.js';
import { collectionHasSportMainFour } from '@/utils/sport-market-types.js';

function pickFirst(...values) {
  for (const v of values) {
    if (v != null && v !== '') return v;
  }
  return null;
}

/** 从 collection / market/get 响应定位当前详情 market 源对象 */
export function pickSportDetailMarketSource(raw, preferredId = '') {
  if (!raw || typeof raw !== 'object') return null;
  const pid = String(preferredId ?? '').trim();
  const markets = Array.isArray(raw.markets) ? raw.markets : [];
  if (pid && markets.length) {
    const hit = markets.find((m) => String(m?.id ?? '') === pid);
    if (hit) return hit;
  }
  if (markets.length) return pickPrimarySportMarket(raw, markets) || markets[0];
  return raw;
}

/** 路由 id 为 collection id 时解析主盘 market id（交易/图表用） */
export function resolveSportPrimaryMarketId(raw, routeId = '') {
  if (!raw || typeof raw !== 'object') return '';
  const rid = String(routeId ?? '').trim();
  const markets = Array.isArray(raw.markets) ? raw.markets : [];
  if (!markets.length) return '';

  if (rid && markets.some((m) => String(m?.id ?? '') === rid)) return rid;

  const collectionId = String(raw?.id ?? raw?.collectionId ?? '').trim();
  if (rid && collectionId && rid === collectionId) {
    return String(pickPrimarySportMarket(raw, markets)?.id ?? markets[0]?.id ?? '').trim();
  }

  return String(pickPrimarySportMarket(raw, markets)?.id ?? markets[0]?.id ?? rid).trim();
}

/** 体育详情：归一化 collection/get 响应（保留 API 原始 markets，含空数组） */
export function normalizeSportMarketDetailPayload(raw, _collectionId = '') {
  if (!raw) return null;

  // collection/get：markets 以 API 为准；缺失/null 视为空盘口，不伪造子 market
  if (Array.isArray(raw.markets)) {
    return raw;
  }

  return { ...raw, markets: [] };
}

function pickRawContestId(raw, preferredMarketId = '') {
  if (!raw || typeof raw !== 'object') return null;
  const markets = Array.isArray(raw.markets) ? raw.markets : [];
  const preferred = String(preferredMarketId ?? '').trim();
  if (preferred && markets.length) {
    const hit = markets.find((m) => String(m?.id ?? '') === preferred);
    const id = pickFirst(hit?.contestId, hit?.contest_id);
    if (id) return id;
  }
  const market = markets[0] ?? raw;
  return pickFirst(
    market?.contestId,
    raw?.contestId,
    market?.contest_id,
    raw?.contest_id,
    market?.sportNanoMatchId,
    raw?.sportNanoMatchId
  );
}

function parseSnapshotScore(scoreStr) {
  const text = String(scoreStr ?? '').trim();
  if (!text || text === '—') return null;
  const m = text.match(/^(\d+)\s*[-:]\s*(\d+)$/);
  if (!m) return null;
  return { home: m[1], away: m[2] };
}

function isDrawOutcome(label) {
  return /^(draw|tie|平局|和局|x)$/i.test(String(label ?? '').trim());
}

function resolveCardVariant(outcomes, prices) {
  const count = Math.max(outcomes.length, prices.length);
  if (count === 2 && !outcomes.some(isDrawOutcome)) return SPORT_CARD_VARIANT.TWO_WAY;
  if (count >= 3 || outcomes.some(isDrawOutcome)) return SPORT_CARD_VARIANT.THREE_WAY;
  if (count === 2) return SPORT_CARD_VARIANT.TWO_WAY;
  return SPORT_CARD_VARIANT.THREE_WAY;
}

function resolveSportFromSnapshot(parsed, fallbackSport) {
  const slug = String(parsed?.sportsSlug ?? '')
    .trim()
    .toLowerCase();
  if (slug === 'basketball') return 'basketball';
  if (slug === 'badminton') return 'badminton';
  if (slug === 'football' || /soccer|足球/.test(slug)) return 'football';

  const sportNorm = String(fallbackSport || '')
    .trim()
    .toLowerCase();
  if (/basketball|nba|篮球/.test(sportNorm)) return 'basketball';
  if (/badminton|羽毛球/.test(sportNorm)) return 'badminton';

  // 有 contestId 且非篮球/羽毛球 → 走足球纳米接口
  if (parsed?.matchId) return 'football';

  return /soccer|football|足球/.test(sportNorm) ? 'football' : 'football';
}

/**
 * 从 market / collection 解析 contestId（sportsPage.markets.contestId）
 */
export function resolveContestId(raw, preferredMarketId = '') {
  const rawContest = pickRawContestId(raw, preferredMarketId);
  return resolveNumericMatchId(rawContest);
}

const SPORTS_SLUG_ORIGIN_RE = /^sports-(\d+)(?:-|$)/i;

/** collection/market slug `sports-{originId}` / `sports-{originId}-mw` → 供应商赛事 id */
export function pickSportsOriginEventId(raw) {
  if (!raw || typeof raw !== 'object') return '';
  const slugs = [raw.slug, raw.collectionSlug];
  if (Array.isArray(raw.markets)) {
    for (const market of raw.markets) slugs.push(market?.slug);
  }
  for (const slug of slugs) {
    const hit = String(slug ?? '')
      .trim()
      .match(SPORTS_SLUG_ORIGIN_RE);
    if (hit) return hit[1];
  }
  return '';
}

/**
 * 是否走体育对阵详情（/sport/live）。
 * 不解析 title 文案；认结构化信号：
 * 1) 双侧主客队名
 * 2) 有效 contestId（collection / markets / 入参）
 * 3) slug `sports-{originId}`
 * 4) 自建足球四大金刚盘口齐全
 */
export function isSportMatchupDetailTarget(raw = {}) {
  if (!raw || typeof raw !== 'object') return false;

  const home = String(raw.homeTeamName || raw.home_team_name || '').trim();
  const away = String(raw.awayTeamName || raw.away_team_name || '').trim();
  if (home && away) return true;

  if (resolveContestId(raw)) return true;
  if (pickSportsOriginEventId(raw)) return true;
  if (collectionHasSportMainFour(raw)) return true;

  return false;
}

/**
 * sport/events 查询 id：contestId → slug origin → collectionId
 * 子盘 contestId 常为空，此时应用 `sports-385743030` 而不是雪花 collection id。
 */
export function resolveSportEventLookupId(raw, preferredMarketId = '') {
  const contest = resolveContestId(raw, preferredMarketId);
  if (contest) return String(contest);
  const origin = pickSportsOriginEventId(raw);
  if (origin) return origin;
  return String(raw?.id ?? raw?.collectionId ?? '').trim();
}

/**
 * 纳米赛事绑定：优先 contestId
 */
export function resolveSportNanoBinding(raw, preferredMarketId = '') {
  if (!raw || typeof raw !== 'object') {
    return { contestId: null, sportNanoMatchId: null, sportNanoSport: null, hasBinding: false };
  }

  const rawContest = pickRawContestId(raw, preferredMarketId);
  const parsed = parseMarketContest(rawContest);
  const contestId = resolveNumericMatchId(rawContest);
  const market = Array.isArray(raw.markets) ? raw.markets[0] : raw;
  const sport = pickFirst(
    parsed?.sportsSlug,
    raw.sportNanoSport,
    market?.sportNanoSport,
    raw.sportType,
    market?.sportType,
    raw.category,
    market?.category
  );

  const sportNanoSport = contestId ? resolveSportFromSnapshot(parsed, sport) : null;

  return {
    contestId,
    sportNanoMatchId: contestId,
    sportNanoSport,
    hasBinding: Boolean(contestId),
    contestSnapshot: parsed?.v === 1 ? parsed : null,
  };
}

/**
 * 从市场数据解析详情页 Hero 展示字段
 * @param {object} raw collection/get 响应
 * @param {string} [routeId] 路由 collection id
 */
export function mapSportMarketHero(raw, routeId = '') {
  if (!raw) return null;

  const collectionId = String(raw?.id ?? raw?.collectionId ?? routeId ?? '').trim();
  const markets = Array.isArray(raw.markets) ? raw.markets : [];
  const hasMarkets = markets.length > 0;
  const primaryMarketId = resolveSportPrimaryMarketId(raw, routeId);
  const market = hasMarkets ? pickSportDetailMarketSource(raw, primaryMarketId) || raw : null;

  const rawContest = pickRawContestId(raw, primaryMarketId);
  const snapshot = parseMarketContest(rawContest);
  const outcomes = hasMarkets ? parseMarketOutcomes(market?.outcomes) : [];
  const outcomePrices = hasMarkets ? parseOutcomePrices(market?.outcomePrices) : [];
  const outcomeOpenPrices = hasMarkets ? parseOutcomePrices(market?.outcomeOpenPrices) : [];
  const cardVariant = hasMarkets
    ? resolveCardVariant(outcomes, outcomePrices)
    : SPORT_CARD_VARIANT.TWO_WAY;

  const homeName =
    pickFirst(
      raw.homeTeamName,
      raw.home_team_name,
      snapshot?.homeTeam && snapshot.homeTeam !== '—' ? snapshot.homeTeam : null
    ) ||
    outcomes[0] ||
    String(raw.title || market?.question || '—');
  const awayName =
    pickFirst(
      raw.awayTeamName,
      raw.away_team_name,
      snapshot?.awayTeam && snapshot.awayTeam !== '—' ? snapshot.awayTeam : null
    ) ||
    outcomes[outcomes.length - 1] ||
    '—';

  const category =
    snapshot?.competition && snapshot.competition !== '—'
      ? snapshot.competition
      : String(raw.category || snapshot?.sportsLabel || raw.sportsName || '').trim();

  const startIso =
    raw.startDate || market?.startDate || (snapshot?.startTime !== '—' ? snapshot?.startTime : '');

  const volume = raw.volume ?? market?.volume ?? raw.liquidity ?? market?.liquidity ?? 0;
  const traders = raw.traders ?? market?.traders ?? 0;
  const binding = resolveSportNanoBinding(raw, primaryMarketId);
  const snapshotScore = parseSnapshotScore(snapshot?.score);

  // 队徽：API homeLogo/awayLogo（Nami → sports_team_display）；禁止用 collection icon/image 冒充两侧队徽
  const homeLogo = String(
    pickFirst(raw.homeLogo, raw.home_logo, snapshot?.homeLogo, snapshot?.home_logo) || ''
  ).trim();
  const awayLogo = String(
    pickFirst(raw.awayLogo, raw.away_logo, snapshot?.awayLogo, snapshot?.away_logo) || ''
  ).trim();

  return {
    collectionId,
    marketId: primaryMarketId || String(market?.id ?? ''),
    contestId: binding.contestId,
    rawContestId: rawContest,
    originEventId: pickSportsOriginEventId(raw),
    title: String(raw.title || market?.question || '').trim(),
    category,
    homeName,
    awayName,
    homeLogo,
    awayLogo,
    startIso,
    volume,
    traders,
    cardVariant,
    outcomes,
    outcomePrices,
    outcomeOpenPrices,
    binding,
    contestSnapshot: binding.contestSnapshot,
    snapshotScore,
  };
}

function parseJsonOrObject(raw, fallback) {
  if (raw == null || raw === '') return fallback;
  if (typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function isYesNoTeamLabel(name) {
  return /^(yes|no)$/i.test(String(name ?? '').trim());
}

/** axios 已解包 data；容错仍包一层 { data } 的响应 */
export function unwrapSportEventPayload(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const inner = raw.data;
  if (
    inner &&
    typeof inner === 'object' &&
    !Array.isArray(inner) &&
    (inner.originEventId != null ||
      inner.homeTeamName != null ||
      inner.liveBundle != null ||
      inner.sport != null)
  ) {
    return inner;
  }
  return raw;
}

function pickEventTeamName(ev, side) {
  const name =
    side === 'home'
      ? pickFirst(ev.homeTeamName, ev.home_team_name, ev.homeTeam?.name, ev.homeName)
      : pickFirst(ev.awayTeamName, ev.away_team_name, ev.awayTeam?.name, ev.awayName);
  const text = String(name || '').trim();
  return text && !isYesNoTeamLabel(text) ? text : '';
}

function pickEventTeamLogo(ev, side) {
  const logo =
    side === 'home'
      ? pickFirst(ev.homeTeamIcon, ev.home_team_icon, ev.homeTeam?.icon, ev.homeLogo, ev.home_logo)
      : pickFirst(ev.awayTeamIcon, ev.away_team_icon, ev.awayTeam?.icon, ev.awayLogo, ev.away_logo);
  return String(logo || '').trim();
}

/** collection/get 无队名/队徽时，用 sport/events Vo 补上（不覆盖已有值） */
export function overlayCollectionWithSportEvent(collection, eventRaw) {
  if (!collection || typeof collection !== 'object') return collection;
  const ev = unwrapSportEventPayload(eventRaw);
  if (!ev) return collection;

  const homeTeamName = pickEventTeamName(ev, 'home');
  const awayTeamName = pickEventTeamName(ev, 'away');
  const homeLogo = pickEventTeamLogo(ev, 'home');
  const awayLogo = pickEventTeamLogo(ev, 'away');
  if (!homeTeamName && !awayTeamName && !homeLogo && !awayLogo) return collection;

  const next = { ...collection };
  if (homeTeamName && !String(next.homeTeamName || next.homeName || '').trim()) {
    next.homeTeamName = homeTeamName;
  }
  if (awayTeamName && !String(next.awayTeamName || next.awayName || '').trim()) {
    next.awayTeamName = awayTeamName;
  }
  if (homeLogo && !String(next.homeLogo || next.home_logo || '').trim()) {
    next.homeLogo = homeLogo;
  }
  if (awayLogo && !String(next.awayLogo || next.away_logo || '').trim()) {
    next.awayLogo = awayLogo;
  }
  return next;
}

/** sport/events → 赛况面板 bundle。无 liveBundle 时用 Vo 记分牌字段（队名/联赛/状态） */
export function mapSportEventLiveBundle(raw) {
  const ev = unwrapSportEventPayload(raw);
  if (!ev) return null;

  const bundle = ev.liveBundle || ev.live_bundle || null;
  const liveFromBundle = parseJsonOrObject(bundle?.liveJson ?? bundle?.live, null);
  const statusRaw = liveFromBundle?.status ?? ev.status;
  const statusNum = Number(statusRaw);
  const status =
    statusRaw === '' || statusRaw == null || Number.isNaN(statusNum) ? statusRaw : statusNum;

  const liveFromVo = {
    homeName: pickEventTeamName(ev, 'home'),
    awayName: pickEventTeamName(ev, 'away'),
    homeLogo: pickEventTeamLogo(ev, 'home'),
    awayLogo: pickEventTeamLogo(ev, 'away'),
    status,
    period: ev.period || '',
    clock: ev.clock || '',
    leagueName: String(pickFirst(ev.competitionName, ev.competition?.name) || '').trim(),
    matchId: String(ev.originEventId || ev.matchId || '').trim(),
  };
  const homeScore = ev.homeScore ?? ev.home_score;
  const awayScore = ev.awayScore ?? ev.away_score;
  if (homeScore != null && homeScore !== '') liveFromVo.homeScore = homeScore;
  if (awayScore != null && awayScore !== '') liveFromVo.awayScore = awayScore;

  const live =
    liveFromBundle && typeof liveFromBundle === 'object' && !Array.isArray(liveFromBundle)
      ? { ...liveFromVo, ...liveFromBundle }
      : liveFromVo;

  return {
    live,
    events: parseJsonOrObject(bundle?.eventsJson ?? ev.events, []),
    stats: parseJsonOrObject(bundle?.statsJson ?? ev.stats, []),
    lineups: parseJsonOrObject(bundle?.lineupsJson ?? ev.lineups, {}),
    playerStats: parseJsonOrObject(bundle?.playerStatsJson ?? ev.playerStats, {}),
    quarterStats: parseJsonOrObject(bundle?.quarterStatsJson ?? ev.quarterStats, {}),
    liveText: parseJsonOrObject(bundle?.liveTextJson ?? ev.liveText ?? ev.livetext, []),
    analysis: parseJsonOrObject(bundle?.analysisJson ?? ev.analysis, {}),
    standing: parseJsonOrObject(bundle?.standingJson ?? ev.standing, {}),
    animationUrl: String(bundle?.animationUrl || ev.animationUrl || '').trim(),
  };
}

export { getMatchId, parseMarketContest };
